<template>
  <div class="transaction-container" :class="{ selected: isSelected && isTransactionEditable }">
    <div @click="isTransactionEditable && toggleActions()">
      <transaction-row :out-of-date=outOfDate :transaction="transaction" :split-from-transaction="splitFromTransaction"
                       :is-editable="isTransactionEditable" :category-id="categoryId" :isSelected="isSelected"
                       :papa-investigation="papaInvestigation" />
    </div>
    <collapse-transition>
      <actions :actions="actions" :category-name="categoryName" v-show="isSelected && isTransactionEditable"/>
    </collapse-transition>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as _ from 'lodash';
import BaseUI from 'base-ui';
import EventBus from '@/event-bus/event-bus';
// eslint-disable-next-line max-len
import MoveTransactionToSaving from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/current-month/components/cashflow-edit/edit-transaction/edit-saving-transaction/MoveTransactionToSaving';
// eslint-disable-next-line max-len
import MoveOneTimeSavingTransactionToFixedSaving from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/current-month/components/cashflow-edit/edit-transaction/edit-saving-transaction/MoveOneTimeSavingTransactionToFixedSaving';
// eslint-disable-next-line max-len
import MoveOneTimeSavingToVariable from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/current-month/components/cashflow-edit/edit-transaction/edit-saving-transaction/MoveOneTimeSavingToVariable';
import transactionUtils from '../../../../../../../../../utils/transaction';
import TransactionRow from './TransactionRow.vue';
import Actions from '../../../../../../../../../base-components/Actions.vue';
import Segment from '../../../../../../../../../Segment';
import ChangeTransactionCategoryPopup from '../../../current-month/components/cashflow-edit/edit-transaction/ChangeTransactionCategoryPopup.vue';
import cashflowViewConsts from '../../../../../../../../../constants/cashflow-view';
import AddTransactionComment from '../../../current-month/components/cashflow-edit/edit-transaction/AddTransactionComment.vue';
import SplitTransactionPopup from '../../../current-month/components/cashflow-edit/edit-transaction/split-transaction/SplitTransactionPopup.vue';

export default {
  name: 'Transaction',
  props: {
    transaction: {
      type: Object,
      required: true,
    },
    canEditCategory: {
      type: Boolean,
      required: true,
    },
    categoryName: {
      type: String,
      required: false,
    },
    categoryId: {
      type: String,
      default: '',
    },
    outOfDate: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: false,
    },
  },
  components: {
    CollapseTransition: BaseUI.CollapseTransition,
    TransactionRow,
    Actions,
  },
  created() {
    EventBus.$on('expand-to-edit', ({ type }) => {
      if (type === this.type) {
        this.expandActionsOf(`${this.transaction.transactionId}${this.categoryName}${this.categoryId}`);
      }
    });
  },
  computed: {
    ...mapState('cashflowView', ['actionsExpandedFor']),
    ...mapGetters('cashflowView', ['transactionsInCurrentCashflow']),
    ...mapGetters('mergeInvestigationQuestions', ['classifiedMergeInvestigation']),
    splitFromTransaction() {
      return this.transaction.splitFrom && _.find(this.transactionsInCurrentCashflow, { transactionId: this.transaction.splitFrom });
    },
    incomeOrExpense() {
      return transactionUtils.incomeOrExpense(this.transaction);
    },
    isExcludedCategory() {
      return this.categoryName === cashflowViewConsts.CATEGORY_NAMES.EXCLUDED_INCOME
          || this.categoryName === cashflowViewConsts.CATEGORY_NAMES.EXCLUDED_EXPENSE;
    },
    isSaving() {
      return this.transaction.isSaving;
    },
    actionsMapping() {
      return {
        editCategory: {
          text: `להזיז את ה${this.incomeOrExpense}`,
          icon: 'edits-move',
          onClick: () => this.openEditCategory(ChangeTransactionCategoryPopup),
        },
        editIncomeCategory: {
          text: `זו לא ${this.incomeOrExpense} משתנה`,
          icon: 'edits-move',
          onClick: () => this.openEditCategory(ChangeTransactionCategoryPopup),
        },
        moveExcludedBack: {
          text: 'להחזיר לתזרים של החודש',
          icon: 'edits-exclude',
          onClick: () => this.openEditCategory(ChangeTransactionCategoryPopup),
        },
        addComment: {
          text: this.transaction.customerComment ? 'לערוך את ההערה' : 'להוסיף הערה',
          icon: 'comment',
          onClick: () => this.openEditCategory(AddTransactionComment),
        },
        splitTransaction: {
          text: this.splitText,
          icon: 'split',
          onClick: () => this.openEditCategory(SplitTransactionPopup),
        },
        moveToSaving: {
          icon: 'edits-is-savings',
          onClick: this.openEditTransaction(MoveTransactionToSaving),
          text: 'זו הפקדה לחיסכון!',
        },
        moveToFixedSaving: {
          icon: 'edits-sub-category',
          onClick: this.openEditTransaction(MoveOneTimeSavingTransactionToFixedSaving),
          text: 'זו הפקדה קבועה לחיסכון',
        },
        notOneTimeSaving: {
          icon: 'edits-not-savings',
          onClick: this.openEditTransaction(MoveOneTimeSavingToVariable),
          text: 'זו לא הפקדה לחיסכון',
        },
      };
    },
    actions() {
      return getActions(this.isExcludedCategory, this.isSaving, this.categoryName, this.transaction.isIncome,
        this.actionsMapping, !!this.transaction.splitFrom);
    },
    isTransactionEditable() {
      return this.canEditCategory && !this.papaInvestigation;
    },
    isSelected() {
      return this.actionsExpandedFor === `${this.transaction.transactionId}${this.categoryName}${this.categoryId}`;
    },
    splitText() {
      if (this.transaction.splitFrom) {
        return 'לערוך את הפיצול';
      }
      return this.transaction.incomeAmount ? 'לפצל את ההכנסה' : 'לפצל את ההוצאה';
    },
    papaInvestigation() {
      return this.classifiedMergeInvestigation(this.transaction.transactionId);
    },
  },
  methods: {
    ...mapActions('editTransaction', ['startEditingTransaction']),
    ...mapActions('cashflowView', ['expandActionsOf']),
    ...mapActions('modalRootStore', ['openModal']),
    toggleActions() {
      if (this.isSelected) {
        this.expandActionsOf(null);
      } else {
        this.expandActionsOf(`${this.transaction.transactionId}${this.categoryName}${this.categoryId}`);
      }
    },
    openEditCategory(component) {
      Segment.trackUserInteraction('EditTransactionClicked', {
        businessName: this.transaction.businessName,
        transactionId: this.transaction.transactionId,
      });
      this.startEditingTransaction({
        transaction: this.transaction,
        categoryName: this.categoryName,
        categoryId: this.categoryId,
      });
      this.openModal({
        component,
      });
    },
    openEditTransaction(popupToOpen) {
      return () => {
        Segment.trackUserInteraction('EditTransactionClicked', {
          businessName: this.transaction.name,
          transactionId: this.transaction.transactionId,
        });
        this.openModal({
          component: popupToOpen,
          props: {
            transaction: this.transaction,
            categoryId: this.categoryId,
            categoryName: this.categoryName,
          },
        });
      };
    },
  },
};

const actionsByType = actionsMapping => {
  const savingActions = [actionsMapping.moveToSaving];
  return {
    variableIncome: [
      actionsMapping.addComment,
      actionsMapping.editIncomeCategory,
      actionsMapping.splitTransaction,
    ],
    variable: [
      actionsMapping.addComment,
      actionsMapping.editCategory,
      actionsMapping.splitTransaction,
      ...savingActions,
    ],
    trackingCategory: [
      actionsMapping.addComment,
      actionsMapping.editCategory,
      actionsMapping.splitTransaction,
      ...savingActions,
    ],
    excluded: [
      actionsMapping.addComment,
      actionsMapping.moveExcludedBack,
      actionsMapping.splitTransaction,
    ],
    oneTimeSaving: [
      actionsMapping.addComment,
      actionsMapping.moveToFixedSaving,
      actionsMapping.notOneTimeSaving,
    ],
  };
};

function getActionsOfType(actionsMapping, isExcludedCategory, isIncome, categoryName, isSaving) {
  const actionsMap = actionsByType(actionsMapping);
  if (isExcludedCategory) {
    return actionsMap.excluded;
  }
  if (isIncome) {
    return actionsMap.variableIncome;
  }

  if (categoryName === cashflowViewConsts.CATEGORY_NAMES.TRACKING_CATEGORY) {
    return actionsMap.trackingCategory;
  }

  if (isSaving) {
    return actionsMap.oneTimeSaving;
  }

  return actionsMap.variable;
}

function savingActions(actionsMapping) {
  return [actionsMapping.moveToSaving, actionsMapping.notOneTimeSaving, actionsMapping.moveToFixedSaving];
}

function getActions(isExcludedCategory, isSaving, categoryName, isIncome, actionsMapping, isSplit) {
  const actions = getActionsOfType(actionsMapping, isExcludedCategory, isIncome, categoryName, isSaving);
  if (isSplit) {
    return _.reject(actions, action => savingActions(actionsMapping).includes(action));
  }
  return actions;
}

</script>

<style scoped lang="scss">
    @import '../../../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';

  .transaction-container {
    transition: background-color 0.3s;
    background-color: $riseup_gray_0;
    &.selected {
      background-color: $riseup_gray_-1;
    }
    border-bottom: 1px solid $riseup_gray_0;
    &:first-of-type {
      box-shadow: inset 0 2px 3px 0 rgba(155, 155, 155, 0.51);
    }
    &:last-of-type {
      border-bottom: none;
    }
  }

</style>
